import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ParentModule } from 'app/pages/A2-utils/ParentClass/parent-module';
import * as moment from 'moment';
import { Subscription } from 'rxjs/Subscription';
import { ReservationService } from '../../reservations.service';
import { SweetAlertHelper } from '../../../../core/helpers/sweet-alert-helper.service';
import { NoteTemplateService } from '../../../../core/services/note-template.service';
import { INoteTemplate } from '../../../../core/interfaces/note.interface';
import { NoteTypeEnum } from '../../../../core/enums/note-type.enum';
import { SweetAlertResult } from 'sweetalert2';
import { FormatCurrencyHelper } from 'app/core/helpers/format-currency-helper';

@Component({
  selector: 'app-provider-billing',
  templateUrl: 'provider-billing.component.html',
  styleUrls: ['provider-billing.component.scss'],
})

export class ProviderBillingComponent extends ParentModule implements OnInit, OnDestroy, OnChanges {
  @Output() onClose = new EventEmitter<boolean>();
  @Input() tripid: any;
  @Input() provideramount: any;
  @Input() totalamountlyft: any;
  @Input() isnegotiatedrate: any;
  @Input() isStandingOrder: boolean;
  @Input() standingOrderDate: any;
  modelDatePicker: any;
  subscriptions: Subscription[] = [];
  formData: FormGroup;
  MAX_TOTAL_AMOUNT_NEGOTIATED = 100;

  constructor(
    private fb: FormBuilder,
    private reservationService: ReservationService,
    protected Inject: Injector,
    private sweetAlertHelper: SweetAlertHelper,
    private noteTemplateService: NoteTemplateService
  ) {
    super('app-edit-reservation-form', Inject);
    this.onCreateForm();
    this.onSubscribeFormValuesChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['tripid'] ||
      changes['provideramount'] ||
      changes['totalamountlyft'] ||
      changes['isnegotiatedrate'] ||
      changes['isStandingOrder']
    ) {
      this.formData.patchValue({
        tripid: this.tripid,
        provideramount: this.provideramount,
        totalamountlyft: this.totalamountlyft,
        isnegotiatedrate: this.isnegotiatedrate,
        addtoallslegs: false
      });
    }

    if (changes['standingOrderDate'] && this.standingOrderDate) {
      this.assignDateToDatePicker(
        this.getDateFormatted(this.standingOrderDate.beginDate),
        this.getDateFormatted(this.standingOrderDate.endDate)
      );
    }
  }

  ngOnInit() {
    this.onDisabledInput();
    this.loadNoteTemplates();
    this.makeSubscriptions();
  }

  private makeSubscriptions(): void {
    const noteControl: AbstractControl = this.formData.get('note');
    this.subscriptions.push(
      this.noteTemplateService.noteTemplateDataByClick$
        .subscribe({
          next: ({description}: INoteTemplate): void => noteControl.setValue(description)
        })
    );
  }

  private loadNoteTemplates(): void {
    this.noteTemplateService.getTemplateNote({ noteCategory: NoteTypeEnum.ProviderNegotiated })
      .subscribe({
          next: (data: INoteTemplate[]) => this.noteTemplateService.sendNoteTemplateData(data),
          error: (e) => this.sweetAlertHelper.captureException(e)
        }
      );
  }

  onCreateForm() {
    this.formData = this.fb.group({
      tripid: [''],
      provideramount: [''],
      totalamountlyft: ['', Validators.required],
      note: ['', Validators.required],
      isnegotiatedrate: [''],
      startdatechanges: [''],
      enddatechanges: [''],
      addtoalllegs: [false]
    });
  }

  onDisabledInput() {
    this.formData.get('provideramount').disable();
  }

  onSubscribeFormValuesChanges() {
    this.formData.get('isnegotiatedrate').valueChanges.subscribe((val) => {
      if (val) {
        this.onEnableValidators('totalamountlyft');
      } else {
        this.onClearValidators('totalamountlyft');
      }
    });
  }

  get isNegotiatedRate(): boolean {
    return this.formData.get('isnegotiatedrate').value;
  }

  onClearValidators(control: string) {
    this.formData.get(control).clearValidators();
    this.formData.get(control).updateValueAndValidity();
  }

  onEnableValidators(control: string) {
    this.formData.get(control).setValidators([Validators.required]);
    this.formData.get(control).updateValueAndValidity();
  }

  assignDateToDatePicker(begin, end) {
    begin = moment(begin);
    end = moment(end);

    this.modelDatePicker = {
      beginDate: {
        year: begin.year(),
        month: begin.month() + 1,
        day: begin.date(),
      },
      endDate: {
        year: end.year(),
        month: end.month() + 1,
        day: end.date(),
      },
    };
  }

  onChangeRangeDate(event: any) {
    if (event) {
      if (!event.formatted) {
        const startDate =
          `${this.modelDatePicker.beginDate.year}-${this.modelDatePicker.beginDate.month}-${this.modelDatePicker.beginDate.day}`;
        const endDate = `${this.modelDatePicker.endDate.year}-${this.modelDatePicker.endDate.month}-${this.modelDatePicker.endDate.day}`;
        this.setDate(startDate, endDate);
      } else {
        this.setDate(event.beginJsDate, event.endJsDate);
      }
    } else {
      this.setDate(null, null);
    }
  }

  setDate(startDate, endDate) {
    this.formData.patchValue({
      startdatechanges: startDate ? moment(startDate).format('MM-DD-YYYY') : '',
      enddatechanges: endDate ? moment(endDate).format('MM-DD-YYYY') : ''
    });
  }

  get totalAmountLyft(): number {
    return this.formData.get('totalamountlyft').value;
  }

  onSave(): void {
    if (
      this.isNegotiatedRate &&
      this.totalAmountLyft >= this.MAX_TOTAL_AMOUNT_NEGOTIATED
    ) {
      this.sweetAlertHelper
        .createCustomAlert({
          html: `Are you sure you want to negotiate for <b>${FormatCurrencyHelper.formatCurrency(this.totalAmountLyft)}</b>?`,
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33'
        })
        .then((result: SweetAlertResult) => {
          if (result && result.value) {
            this.toConfirm();
          }
        });
    } else {
      this.toConfirm();
    }
  }

  toConfirm() {
    const formData = Object.assign({}, this.formData.getRawValue());
    if (formData.tripid) {
      delete formData.provideramount;
      if (!this.isAllowedAction('Allow So Changes By Date') || !this.isStandingOrder) {
        delete formData.startdatechanges;
        delete formData.enddatechanges;
      }
      if (this.isStandingOrder) {
        this.subscriptions.push(
          this.reservationService
            .putProviderNegociateRateSo(formData)
            .subscribe(
              (resp) => {
                if (resp.success) {
                  this.succesMessage(resp.message);
                }
              },
              (error) => {
                this.sweetAlertHelper.createCustomAlert({title: 'Error!', text: error.message, type: 'error'});
              }
            )
        );
      } else {
        this.subscriptions.push(
          this.reservationService.putProviderNegociateRate(formData).subscribe(
            (resp) => {
              if (resp.success) {
                this.succesMessage(resp.message);
              }
            },
            (error) => {
              this.sweetAlertHelper.createCustomAlert({title: 'Error!', text: error.message, type: 'error'});
            }
          )
        );
      }
    }
  }

  succesMessage(message: any) {
    if (message) {
      this.sweetAlertHelper.createCustomAlert({title: 'Successfully registered!', text: message, type: 'success'}).then(() => {
        this.onClose.emit(true);
      });
    }
  }

  onCancel() {
    this.formData.reset();
    this.onClose.emit(false);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  getDateFormatted(date) {
    const newDate = date.month + '-' + date.day + '-' + date.year;
    return new Date(newDate);
  }

}



// WEBPACK FOOTER //
// ./src/app/pages/trip-reservations/edit-reservation/provider-billing/provider-billing.component.ts